import React, { Fragment, ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Link,
    Skeleton,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Tooltip,
} from '@mui/material';

import { useAppSelector } from '../../state/hooks';
import { Shipper } from '../../interfaces/services/shippers';
import { ApiStatus } from '../../utils/enums';
import { truncateId } from '../../utils/formattingUtils';
import { buildShipperLink } from '../../utils/navigationHelpers';
import { useFilters } from '../Filters/useFilters';

const TOP_SHIPPER_COUNT = 3;

enum KpiShipperTabs {
    TopShippers = 'TopShippers',
    BottomShippers = 'BottomShippers',
}

const KPIShipperList = ({
    metric,
    metricDisplayName,
    noData,
    comparatorFn,
    formatFn,
}: {
    /** metric to display */
    metric: keyof Shipper;
    /** display name used as the column header */
    metricDisplayName: string;
    /** if there was an error with the KPI */
    noData: boolean;
    /** function used to sort shippers from best to worst for the provided metric */
    comparatorFn?: (a: Shipper, b: Shipper) => number;
    /** formats the value */
    formatFn?: (shipper: Shipper) => string | null;
}): React.JSX.Element => {
    const [selectedTab, setSelectedTab] = useState(KpiShipperTabs.TopShippers);
    const filters = useFilters();

    const shippersApiStatus = useAppSelector((state) => { return state.shippers.shippersApiStatus; });
    const shippers = useAppSelector((state) => { return state.shippers.shippers; });

    const loading = shippersApiStatus === ApiStatus.Idle || shippersApiStatus === ApiStatus.Loading;

    let topShippers: Shipper[] = [];

    topShippers = shippers.map((shipper) => { return shipper; });
    if (comparatorFn) {
        topShippers.sort(comparatorFn);
    } else {
        topShippers.sort((a: Shipper, b: Shipper) => {
            return (b[metric] as number) - (a[metric] as number);
        });
    }

    if (selectedTab === KpiShipperTabs.BottomShippers) {
        topShippers = topShippers.reverse().slice(0, TOP_SHIPPER_COUNT);
    } else {
        topShippers = topShippers.slice(0, TOP_SHIPPER_COUNT);
    }

    const renderShipperName = (shipper: Shipper): React.JSX.Element => {
        if (loading) {
            return <Skeleton />;
        }

        if (shipper.pickup_party_name === 'No Data') {
            return <span>No Data</span>;
        }

        return (
            <Tooltip title={shipper.pickup_party_name}>
                <Link
                    component={RouterLink}
                    to={buildShipperLink(shipper.pickup_party_id, filters)}
                >
                    {truncateId(shipper.pickup_party_name)}
                </Link>
            </Tooltip>
        );
    };

    const renderShipperValue = (shipper: Shipper): ReactNode => {
        if (loading) {
            return <Skeleton />;
        }

        if (formatFn && !noData) {
            return formatFn(shipper);
        }

        return shipper[metric];
    };

    const renderTableBody = (): ReactNode => {
        if (loading) {
            return Array.from(new Array(TOP_SHIPPER_COUNT)).map((value, index) => {
                return (
                    <TableRow key={index}>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                    </TableRow>
                );
            });
        }

        if (noData) {
            return (
                <TableRow>
                    <TableCell colSpan={2}>No Data</TableCell>
                </TableRow>
            );
        }

        return topShippers.map((shipper, index) => {
            return (
                <TableRow key={`${metric}-shipper-${index}`}>
                    <TableCell>
                        {renderShipperName(shipper)}
                    </TableCell>
                    <TableCell align='right'>
                        {renderShipperValue(shipper)}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <Fragment>
            <Tabs
                variant='fullWidth'
                value={selectedTab}
                onChange={(e, newValue): void => { setSelectedTab(newValue); }}
                indicatorColor='primary'
            >
                <Tab value={KpiShipperTabs.TopShippers} label='Top Shippers' />
                <Tab value={KpiShipperTabs.BottomShippers} label='Bottom Shippers' />
            </Tabs>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            Shipper Name
                        </TableCell>
                        <TableCell align='right'>
                            {metricDisplayName || metric}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody style={{ overflow: 'auto' }}>
                    {renderTableBody()}
                </TableBody>
            </Table>
        </Fragment>
    );
};

export default KPIShipperList;
